// ==========================================
// STEP 6: Create hooks/usePosts.js
// ==========================================
import { useState, useCallback, useEffect } from 'react';
import { useAPI } from './useAPI';

export const usePosts = (initialCategory = 'feed') => {
  const { apiCall } = useAPI();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalPosts, setTotalPosts] = useState(0);
  const [hasMore, setHasMore] = useState(false);

  const loadPosts = useCallback(async (category) => {
    setLoading(true);
    try {
      const response = await apiCall(`/posts?category=${category}`);
      setPosts(response.posts || []);
      setTotalPosts(response.total || 0);
      setHasMore(response.has_more || false);
    } catch (err) {
      console.error('Error loading posts:', err);
      setPosts([]);
    } finally {
      setLoading(false);
    }
  }, [apiCall]);

  const createPost = useCallback(async (formData) => {
    try {
      const response = await apiCall('/posts/upload', {
        method: 'POST',
        body: formData,
        formData: true,
      });
      setPosts([response, ...posts]);
      return response;
    } catch (err) {
      throw err;
    }
  }, [apiCall, posts]);

  const updatePost = useCallback(async (postId, updates) => {
    try {
      const response = await apiCall(`/posts/${postId}`, {
        method: 'PUT',
        body: JSON.stringify(updates),
      });
      setPosts(posts.map(p => p.id === postId ? response : p));
      return response;
    } catch (err) {
      throw err;
    }
  }, [apiCall, posts]);

  const deletePost = useCallback(async (postId) => {
    try {
      await apiCall(`/posts/${postId}`, { method: 'DELETE' });
      setPosts(posts.filter(p => p.id !== postId));
    } catch (err) {
      throw err;
    }
  }, [apiCall, posts]);

  useEffect(() => {
    loadPosts(initialCategory);
  }, [initialCategory, loadPosts]);

  return {
    posts,
    loading,
    totalPosts,
    hasMore,
    loadPosts,
    createPost,
    updatePost,
    deletePost,
  };
};